/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/*
AdobePatentID="P8158-US"
*/

import PersonalizedHome from '@cchome/home-components/src/components/PersonalizedHome';
import UserTypeUtil from '@cchome/home-components/src/components/PersonalizedHome/UserTypeUtil';
import type {History} from 'history';
import React, {Component, Fragment} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {renderRoutes} from 'react-router-config';
import type {RouteComponentProps} from 'react-router-dom';
import {fetchMLAppRanking} from '../../actions/predictActions';
import {fetchQuickLinks} from '../../actions/ucsActions';
import DiscoverHome from '../../components/DiscoverHome/DiscoverHome';
import LearnHome from '../../components/LearnHome/LearnHome';
import {addMiniPlanModal} from '../../components/MiniPlanModal/MiniPlanModal';
import showInterstitial from '../../components/QuickActions/ccxInterstitial';
import postDownload from '../../components/QuickActions/postDownload';
import addQuickTaskModal from '../../components/QuickActions/quickTaskModal';
// import saveToLibraryManager from '../../components/QuickActions/saveToLibraryManager';
import SingleAppQuickLinks from '../../components/QuickLinks/SingleAppQuickLinks';
import RecentWork from '../../components/RecentWork/RecentWork';
import SurveyPopup from '../../components/SurveyPopup/SurveyPopup';
import './Home.scss';
import {MAXBanner} from 'discover-hub';
import PostIMSFetchService from '../../services/PostIMSFetchService';
import MAXBannerAnalytics from './Analytics/MAXBannerAnalytics';

declare let adobeIMS: AdobeIMS;

type State = {
	showRecentsOnTop: boolean;
	notificationStyle: any;
};

interface HomeProps extends RouteComponentProps {
	locale: string;
	env: string;
	route: {
		routes: any[];
	};
	profileFetchFinished: boolean;
	customerSegment: string;
	storageQuota: number;
	fetchMLAppRanking: Function;
	checkRecentDataStatus: symbol;
	planCode: string;
	intl: any;
	history: History;
	language: string;
	location: any;
	leftRailOpen: boolean;
	fetchQuickLinks: Function;
	quickLinks: Array<QuickLinksArrayContent>;
	fetchQuickLinksComplete: boolean;
	fetchQuickLinksError: boolean;
	notifications: Array<any>;
}

export class Home extends Component<HomeProps, State> {
	constructor(props: HomeProps) {
		super(props);
		this.state = {
			showRecentsOnTop: true,
			notificationStyle: {},
		};

		if (window.cchome.util.hasFeature('enable_logger')) {
			console.time('recent_new_cached');
			console.time('recent_new_uncached_start');
			console.time('recent_new_uncached_end');
		}
	}

	componentWillMount() {
		if (window.cchome.util.isIE()) {
			this.props.history.push('/apps');
		}

		// fetch app dwnload cluster
		if (window.cchome.util.hasFeature('20190412_Home_ML')) {
			this.props.fetchMLAppRanking();
		}

		if (window.cchome.util.hasFeature('ucs-quicklinks')) {
			this.props.fetchQuickLinks();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.notifications !== this.props.notifications) {
			this.getHomeContainerStyle();
		}
	}

	componentDidMount() {
		this.getHomeContainerStyle();
		window.addEventListener('breakpointChanged', this.getHomeContainerStyle.bind(this));
	}

	componentWillUnmount() {
		window.removeEventListener('breakpointChanged', this.getHomeContainerStyle.bind(this));
	}

	shiftRecentsToBottom = () => {
		this.setState({
			showRecentsOnTop: false,
		});
	};

	canDisplayRecentAssets = () => {
		// TODO - check if IMS checks can be replaced by isUserSignedIn method
		const userId = window.cchome.util.getUserId();
		return (
			!(
				this.props.customerSegment
				&& this.props.customerSegment.toLowerCase() === 'enterprise'
				&& this.props.storageQuota === 0
			)
			&& adobeIMS
			&& adobeIMS.getAccessToken()
			&& adobeIMS.getUserProfile()
			&& userId
		);
	};

	canDisplayLearnHome = () => {
		// const isEnterprise = this.props.customerSegment && this.props.customerSegment.toLowerCase() === 'enterprise';
		const isk12 = this.props.planCode && this.props.planCode.includes('k12');
		return !isk12 && this.getUserType() !== 'CCE_Free' && this.getUserType() !== 'Communicator';
	};

	renderRecentAndLearn = () => {
		const canDisplayRecentAssets = this.canDisplayRecentAssets();
		const canDisplayLearnHome = this.canDisplayLearnHome();
		const {showRecentsOnTop} = this.state;
		const LearnComponent
			= canDisplayLearnHome && !window.cchome.util.hasFeature('discover_on_home') ? (
				<LearnHome
					env={this.props.env}
					locale={this.props.locale}
					customerSegment={this.props.customerSegment}
					onTop
					showSkeletonUI={false}
					location={this.props.location}
					intl={this.props.intl}
					history={this.props.history}
				/>
			) : null;

		const DiscoverHomeRowComponent = canDisplayLearnHome ? (
			<DiscoverHome
				env={this.props.env}
				locale={this.props.locale}
				location={this.props.location}
				intl={this.props.intl}
				history={this.props.history}
			/>
		) : null;

		const RecentComponent = canDisplayRecentAssets ? (
			<RecentWork
				key='recent-container'
				shiftRecentsToBottom={this.shiftRecentsToBottom}
				env={this.props.env}
				locale={this.props.locale}
				createSpacesCallback={() => {}}
			/>
		) : null;
		return (
			<Fragment>
				{showRecentsOnTop ? [RecentComponent, LearnComponent] : [LearnComponent, RecentComponent]}
				{DiscoverHomeRowComponent}
			</Fragment>
		);
	};

	isAcrobatDC = (): boolean => {
		const {planCode} = this.props;
		return (
			planCode === 'apcc_direct_individual'
			|| planCode === 'apap_direct_individual'
			|| planCode === 'apro_direct_individual'
			|| planCode === 'acro_direct_individual'
		);
	};

	getUserType = (): string | void => UserTypeUtil.getUserType();

	handleRouteChange = (route: string) => {
		const {history} = this.props;
		history.push(route);
	};

	showJarvis() {
		document.body && document.body.classList.remove('hide-jarvis');
	}

	hideJarvis = () => {
		document.body && document.body.classList.add('hide-jarvis');
	};

	isUserAllowedForSurveyPopup = () => {
		let isUserAllowed = false; // By default, assume the user is not allowed.
		const isTeam = this.props.planCode && this.props.planCode.indexOf('team') >= 0;
		const isEnterprise = this.props.planCode && this.props.planCode.indexOf('enterprise') >= 0;
		const isEnglish = this.props.language === 'en';
		const featureFlag = window.cchome.util.hasFeature('20200131_SurveyPopup');

		/* istanbul ignore else */
		// planCode could be undefined, so adding another check for planCode to make sure it is valid before assuming user is Enterprise or Team
		if (featureFlag && !isTeam && !isEnterprise && isEnglish) {
			const userID = window.cchome.util.getUserId();
			const userClosedSurvey
				= window.cchome.util.storageAvailable('localStorage')
				&& localStorage.getItem(`closedSurveyPopup-${userID}`);

			/* istanbul ignore else */
			if (!userClosedSurvey) {
				this.hideJarvis();
				isUserAllowed = true;
			}
		}
		return isUserAllowed;
	};

	renderSurveyPopup = () => {
		if (!window.cchome.util.isMobileOrTabletBrowser() && this.isUserAllowedForSurveyPopup()) {
			return <SurveyPopup title='v1' showJarvis={this.showJarvis} />;
		}
		this.showJarvis();
		return null;
	};

	isNUJCohort = () => {
		let nujVariant;
		let isUserNUJProfileSet;
		let isCoreRuleMet = true;

		if (window.cchome.config.isFGServiceFail) {
			nujVariant = 'na';
			return false;
		}
		const userNUJProfileCtrlFeature = window.cchome.util.hasFeature('temp_nuj_m2_profile_set_ctrl');
		const userNUJProfileTestFeature = window.cchome.util.hasFeature('temp_nuj_m2_profile_set_test');
		const userCoreRulesFeature = window.cchome.util.hasFeature('temp_nuj_m2_core_rules');
		if (userNUJProfileCtrlFeature || userNUJProfileTestFeature) {
			// User's NUJ profile is set to ctrl
			isUserNUJProfileSet = true;
			nujVariant = userNUJProfileCtrlFeature ? 'control' : 'test';

			// If NUJ variant is test, then check the core rules flag to ensure that these are met. Only then NUJ will be given.
			if (nujVariant === 'test' && !userCoreRulesFeature) {
				nujVariant = 'not-in-cohort';
				isCoreRuleMet = false;
			}
		}
		else {
			// User's NUJ profile is not set
			if (window.cchome.util.hasFeature('temp_bypass_nuj_m2_split')) {
				// Bypass NUJ M2 check
				nujVariant = 'test';
			}
			else {
				const userNUJCohortFeature
					= window.cchome.config.featuresAnalyticsParams
					&& window.cchome.config.featuresAnalyticsParams.find(
						feature => feature.f_key === 'temp_nuj_m2_eligible',
					);
				if (userNUJCohortFeature) {
					// Recvd. the flag, which means the user is either in test or control
					nujVariant = window.cchome.util.getFeatureFlagVariant(userNUJCohortFeature);
				}
				else {
					// Not recvd. the flag, which means the user is not in cohort
					nujVariant = 'not-in-cohort';
				}
			}
		}

		return (
			(nujVariant === 'test' || window.cchome.util.hasFeature('20200224_ResumeFlow_Bypass'))
			&& this.props.language === 'en'
		);
	};

	getHomeContainerStyle = () => {
		const style: any = {};
		const breakpoint = window.cchome.util.currentBreakpoint;
		let breakpointMod = 0;
		switch (breakpoint) {
			case 'L':
			case 'LM':
			case 'M':
			case 'MS':
				breakpointMod = 64;
				break;
			case 'S':
			case 'SS':
				breakpointMod = 32;
				break;
			case 'SX':
				breakpointMod = 48;
				break;
		}
		if (document.getElementsByClassName('notification-module-adjustment')[0]) {
			const top = document.getElementsByClassName('notification-module-adjustment')[0].clientHeight + breakpointMod;
			style.marginTop = `${top}px`;
		}

		this.setState({
			notificationStyle: style,
		});
	};

	render() {
		const containerStyle = this.state.notificationStyle;
		const hasNotification = this.props.notifications.length;

		if (window.cchome.util.isIE()) {
			return (
				<div className='ie-error-page'>
					<p>{this.props.intl.formatMessage({id: 'app.ie11.error.message'})}</p>
				</div>
			);
		}

		const showUCSQuicklinks = window.cchome.util.hasFeature('ucs-quicklinks');

		const singleAppQuickLinksProps = {
			language: this.props.language,
			planCode: this.props.planCode,
			entitlement: this.getUserType(),
			isAcrobatDC: this.isAcrobatDC(),
			history: this.props.history,
			quickLinks: showUCSQuicklinks ? this.props.quickLinks : [],
			fetchQuickLinksComplete: showUCSQuicklinks ? this.props.fetchQuickLinksComplete : true,
			fetchQuickLinksError: showUCSQuicklinks ? this.props.fetchQuickLinksError : false,
			fetchQuickLinks,
		};

		const callBacksFromCCHReact = {
			postDownload,
			showInterstitial,
			addQuickTaskModal,
		};

		const hasLeftRail = window.cchome.util.hasFeature('apphomes');

		const isOpen = hasLeftRail && this.props.leftRailOpen && document.body.clientWidth > 960;

		const showMax2021Banner = window.cchome.util.hasFeature('2021_max_banner');

		return (
			<>
				<div className='home-container-wrapper-v3' style={hasNotification ? containerStyle : {}}>
					<PersonalizedHome
						callBacksFromCCHReact={callBacksFromCCHReact}
						handleRouteChange={this.handleRouteChange.bind(this)}
						useML={window.cchome.util.hasFeature('home_components_ml')}
						parent={this}
						addMiniPlanModal={addMiniPlanModal.bind(this)}
						leftRailOpen={isOpen}
						preFetchedRestAPIResponses={PostIMSFetchService.getResponseForKey}
					>
						<div className='home-container-discover-home'>
							{/* // TODO: To be removed once we switch to using UCS service response */}
							<SingleAppQuickLinks {...singleAppQuickLinksProps} />
							<RecentWork
								key='recent-container'
								env={this.props.env}
								shiftRecentsToBottom={this.shiftRecentsToBottom}
								locale={this.props.locale}
								createSpacesCallback={() => {}}
							/>
							<DiscoverHome
								env={this.props.env}
								locale={this.props.locale}
								location={this.props.location}
								intl={this.props.intl}
								history={this.props.history}
							/>
						</div>
					</PersonalizedHome>
					{renderRoutes(this.props.route.routes)}
				</div>
				{showMax2021Banner && (
					<MAXBanner
						intl={this.props.intl}
						analytics={{
							postRender: () => {
								MAXBannerAnalytics.postRender();
							},
							postClick: (_e, setEventSubtype: string) => {
								MAXBannerAnalytics.postClick(setEventSubtype);
							},
						}}
					/>
				)}
			</>
		);
	}
}

function mapStateToProps(state) {
	return {
		customerSegment: state.profile.subscriptionInfo.customerSegment,
		storageQuota: state.profile.storage.storage_quota,
		predictAppRanking: state.predict.predictAppRanking,
		profileFetchFinished: state.profile.profileFetchFinished,
		planCode: state.profile.subscriptionInfo.planCode,
		language: state.app.language,
		leftRailOpen: state.uistore.leftRailOpen,
		quickLinks: state.userContent.quickLinks,
		fetchQuickLinksComplete: state.userContent.ucsFetchFinished,
		fetchQuickLinksError: state.userContent.ucsTimeoutOrError,
		notifications: state.appNotifications.notifications,
	};
}

export default {
	component: connect(mapStateToProps, {
		fetchMLAppRanking,
		fetchQuickLinks,
	})(injectIntl(Home)),
};
